import React, { useState, useEffect } from 'react'
import SidePanel from '@/components/ui/SidePanel'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import DepartmentSelect from '@/components/ui/DepartmentSelect'
import MemberSelect from '@/components/ui/MemberSelect'
import RequiredLabel from '@/components/ui/RequiredLabel'
import { designTokens } from '@/utils/design-tokens'
import { PerformanceService } from '@/api/generated/services/PerformanceService'
import { PerformanceItemService } from '@/api/generated/services/PerformanceItemService'
import { DepartmentService } from '@/api/generated/services/DepartmentService'
import { OrgMemberService } from '@/api/generated/services/OrgMemberService'
import type { PerformanceFullUpdateRequest } from '@/api/generated/models/PerformanceFullUpdateRequest'
import type { PerformanceItemUpdateRequest } from '@/api/generated/models/PerformanceItemUpdateRequest'
import type { PerformanceItemResponse } from '@/api/generated/models/PerformanceItemResponse'
import type { PerformanceTreeNode } from '../types'
import type { DepartmentResponse } from '@/api/generated/models/DepartmentResponse'
import type { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse'
import type { PaginatedResponse } from '@/api/generated/models/PaginatedResponse'
import { useAppSelector } from '@/store/hooks'

interface PerformanceEditPanelProps {
  isOpen: boolean
  onClose: () => void
  performanceData: PerformanceTreeNode | null
  onSuccess?: () => void
}

interface PerformanceItem {
  id?: string
  name: string
  target: number | null
  achievement: number | null
}

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className="pb-2 border-b"
    style={{
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3 style={{
      fontFamily: designTokens.typography.fontFamily.sans[0],
      fontSize: '18px',
      fontWeight: 700,
      color: designTokens.colors.gray[6]
    }}>
      {title}
    </h3>
  </div>
)

const InfoField: React.FC<{
  label: string | React.ReactNode
  value: string | React.ReactNode
  fullWidth?: boolean
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      {typeof label === 'string' ? (
        <span style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}>
          {label}
        </span>
      ) : (
        label
      )}
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}>
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const PerformanceEditPanel: React.FC<PerformanceEditPanelProps> = ({
  isOpen,
  onClose,
  performanceData,
  onSuccess
}) => {
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // Form state
  const [performanceName, setPerformanceName] = useState('')
  const [unit, setUnit] = useState('')
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null)
  const [selectedDepartmentName, setSelectedDepartmentName] = useState<string | null>(null)
  const [assigneeId, setAssigneeId] = useState<string | null>(null)
  const [assigneeName, setAssigneeName] = useState<string | null>(null)
  const [performanceItems, setPerformanceItems] = useState<PerformanceItem[]>([])

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [departmentError, setDepartmentError] = useState<string | null>(null)

  // Department and member data
  const [departments, setDepartments] = useState<DepartmentResponse[]>([])
  const [isDepartmentsLoading, setIsDepartmentsLoading] = useState(false)
  const [departmentsError, setDepartmentsError] = useState<string | null>(null)

  const [workspaceMembers, setWorkspaceMembers] = useState<OrgMemberResponse[]>([])
  const [isMembersLoading, setIsMembersLoading] = useState(false)
  const [membersError, setMembersError] = useState<string | null>(null)

  // Fetch performance details with items when panel opens
  useEffect(() => {
    const fetchPerformanceDetails = async () => {
      if (!performanceData || !isOpen) return

      setIsLoadingData(true)
      try {
        // Fetch performance details
        const performanceResponse = await PerformanceService.appsPerformanceViewsGetPerformance({
          performanceId: performanceData.id
        })

        // Fetch performance items separately
        const itemsResponse = await PerformanceItemService.appsPerformanceViewsListPerformanceItems({
          performanceId: performanceData.id
        })

        // Set form fields
        setPerformanceName(performanceResponse.name || '')
        setUnit(performanceResponse.unit || '')
        setSelectedDepartmentId(performanceResponse.department_id || null)
        setAssigneeId(performanceResponse.assignee_id || null)

        // Set department name from performanceData
        if (performanceData.department) {
          setSelectedDepartmentName(performanceData.department.name)
        }

        // Set assignee name from performanceData
        if (performanceData.assignee) {
          setAssigneeName(performanceData.assignee.name)
        }

        // Set performance items
        const items: PerformanceItem[] = itemsResponse.map((item: PerformanceItemResponse) => ({
          id: item.id,
          name: item.name,
          target: item.target_figure || 0,
          achievement: item.done_figure || 0
        }))
        setPerformanceItems(items)

      } catch (err) {
        console.error('Failed to fetch performance details:', err)
        setError('세부지표 정보를 불러오는데 실패했습니다.')
      } finally {
        setIsLoadingData(false)
      }
    }

    fetchPerformanceDetails()
  }, [performanceData, isOpen])

  // Fetch departments when workspace changes
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!selectedWorkspace?.id) return

      setIsDepartmentsLoading(true)
      setDepartmentsError(null)

      try {
        const response: PaginatedResponse = await DepartmentService.appsDepartmentViewsListDepartments({
          workspaceId: selectedWorkspace.id,
          page: 1,
          perPage: 100
        })

        setDepartments(response.data as DepartmentResponse[] || [])
      } catch (err) {
        console.error('Failed to fetch departments:', err)
        setDepartmentsError('부서 목록을 불러오는데 실패했습니다.')
      } finally {
        setIsDepartmentsLoading(false)
      }
    }

    fetchDepartments()
  }, [selectedWorkspace?.id])

  // Fetch workspace members when workspace changes
  useEffect(() => {
    const fetchWorkspaceMembers = async () => {
      if (!selectedWorkspace?.id) return

      setIsMembersLoading(true)
      setMembersError(null)

      try {
        const response: PaginatedResponse = await OrgMemberService.appsOrganizationViewsListOrgMembers({
          orgId: selectedWorkspace.org_id,
          workspaceId: selectedWorkspace.id,
          page: 1,
          perPage: 1000
        })

        // Filter org members for the current workspace
        const orgMembers = response.data as OrgMemberResponse[] || []
        const members = orgMembers.filter(member =>
          member.workspaces?.some(ws => ws.id === selectedWorkspace.id)
        )

        setWorkspaceMembers(members)
      } catch (err) {
        console.error('Failed to fetch workspace members:', err)
        setMembersError('멤버 목록을 불러오는데 실패했습니다.')
      } finally {
        setIsMembersLoading(false)
      }
    }

    fetchWorkspaceMembers()
  }, [selectedWorkspace?.id, selectedWorkspace?.org_id])

  // Department selection handler
  const handleDepartmentSelect = (departmentId: string, departmentName: string) => {
    setSelectedDepartmentId(departmentId)
    setSelectedDepartmentName(departmentName)
    setDepartmentError(null)
  }

  // Assignee selection handler
  const handleAssigneeSelect = (memberId: string, memberName: string, _memberEmail: string) => {
    setAssigneeId(memberId || null)
    setAssigneeName(memberName || null)
  }

  // Performance items management
  const handleAddItem = () => {
    const newItem: PerformanceItem = {
      name: '',
      target: null,
      achievement: null
    }
    setPerformanceItems([...performanceItems, newItem])
  }

  const handleRemoveItem = (index: number) => {
    setPerformanceItems(performanceItems.filter((_, i) => i !== index))
  }

  const handleItemChange = (index: number, field: keyof PerformanceItem, value: string | number | null) => {
    const updatedItems = performanceItems.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    )
    setPerformanceItems(updatedItems)
  }

  // Calculate totals
  const totalTarget = performanceItems.reduce((sum, item) => sum + (item.target || 0), 0)
  const totalAchievement = performanceItems.reduce((sum, item) => sum + (item.achievement || 0), 0)

  const handleCancel = () => {
    setPerformanceName('')
    setUnit('')
    setSelectedDepartmentId(null)
    setSelectedDepartmentName(null)
    setAssigneeId(null)
    setAssigneeName(null)
    setPerformanceItems([])
    setError(null)
    setDepartmentError(null)
    onClose()
  }

  const handleSave = async () => {
    // Validation
    if (!performanceName.trim()) {
      setError('성과명은 필수입니다.')
      return
    }

    if (!selectedDepartmentId) {
      setError('부서를 선택해주세요.')
      return
    }

    if (!assigneeId) {
      setError('담당자를 선택해주세요.')
      return
    }

    // Validate items
    for (const item of performanceItems) {
      if (!item.name.trim()) {
        setError('모든 항목의 이름을 입력해주세요.')
        return
      }
    }

    if (!performanceData) return

    setIsSubmitting(true)
    setError(null)
    setDepartmentError(null)

    try {
      // Build items array with proper CRUD handling
      const items: PerformanceItemUpdateRequest[] = performanceItems.map(item => ({
        id: item.id || null, // id exists = update, no id = create
        name: item.name.trim(),
        achieve_type: 'figure', // Always figure mode for now
        target_figure: item.target || 0,
        done_figure: item.achievement || 0,
        manual_achieved: null
      }))

      const updateRequest: PerformanceFullUpdateRequest = {
        name: performanceName.trim(),
        unit: unit.trim() || null,
        assignee_id: assigneeId,
        department_id: selectedDepartmentId,
        items: items
      }

      await PerformanceService.appsPerformanceViewsUpdatePerformanceFull({
        performanceId: performanceData.id,
        requestBody: updateRequest
      })

      // Success
      onSuccess?.()
      onClose()
    } catch (err) {
      console.error('Failed to update performance:', err)

      if (err && typeof err === 'object' && 'status' in err) {
        switch (err.status) {
          case 400:
            setError('입력 데이터가 올바르지 않습니다.')
            break
          case 401:
            setError('인증이 만료되었습니다. 다시 로그인해주세요.')
            break
          case 404:
            setError('세부지표를 찾을 수 없습니다.')
            break
          case 409:
            setError('이미 존재하는 세부지표명입니다.')
            break
          case 422:
            setError('입력 데이터의 형식이 올바르지 않습니다.')
            break
          case 500:
            setError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
            break
          default:
            setError('세부지표 수정에 실패했습니다. 다시 시도해주세요.')
        }
      } else {
        setError('세부지표 수정에 실패했습니다. 다시 시도해주세요.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isSubmitting}
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSave}
        disabled={isSubmitting || !performanceName.trim() || !selectedDepartmentId || !assigneeId}
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  if (!performanceData) return null

  const getHierarchyDisplay = () => {
    const { orgHierarchy } = performanceData
    // 세부지표: 사업명 > ... > 성과지표명
    return orgHierarchy.slice(0, -1).join(' > ') || '-'
  }

  return (
    <>
      <SidePanel
        isOpen={isOpen}
        onClose={handleCancel}
        title="세부지표 수정"
        width={800}
        headerActions={headerActions}
      >
        <div
          className="flex flex-col space-y-6"
          style={{
            padding: '24px',
          }}
        >
          {isLoadingData && (
            <div
              className="p-3 rounded"
              style={{
                backgroundColor: designTokens.colors.gray[0],
                border: `1px solid ${designTokens.colors.gray[1]}`,
              }}
            >
              <span
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[5],
                }}
              >
                데이터를 불러오는 중...
              </span>
            </div>
          )}

          {error && (
            <div
              className="p-3 rounded"
              style={{
                backgroundColor: '#FEE2E2',
                border: `1px solid ${designTokens.colors.error.DEFAULT}`,
              }}
            >
              <span
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.error.DEFAULT,
                }}
              >
                {error}
              </span>
            </div>
          )}

          {/* 계층 구조 */}
          <div className="space-y-2">
            <SectionHeader title="계층 구조" />
            <div className="space-y-4" style={{ padding: '0px 4px' }}>
              <InfoField
                label="상위 경로"
                value={getHierarchyDisplay()}
              />
            </div>
          </div>

          {/* 기본 정보 */}
          <div className="space-y-2">
            <SectionHeader title="기본 정보" />
            <div className="space-y-4" style={{ padding: '0px 4px' }}>
              {/* 성과명 */}
              <div className="flex items-center gap-5">
                <div className="w-37 flex-shrink-0">
                  <span style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    fontWeight: 400,
                    color: designTokens.colors.gray[6]
                  }}>
                    성과명 <span style={{ color: designTokens.colors.error.DEFAULT }}>*</span>
                  </span>
                </div>
                <div className="flex-1">
                  <Input
                    value={performanceName}
                    onChange={(e) => setPerformanceName(e.target.value)}
                    placeholder="성과명을 입력하세요"
                    disabled={isSubmitting || isLoadingData}
                  />
                </div>
              </div>

              {/* 단위 */}
              <div className="flex items-center gap-5">
                <div className="w-37 flex-shrink-0">
                  <span style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    fontWeight: 400,
                    color: designTokens.colors.gray[6]
                  }}>
                    단위
                  </span>
                </div>
                <div className="flex-1">
                  <Input
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    placeholder="단위를 입력하세요 (예: 건, 원, %)"
                    disabled={isSubmitting || isLoadingData}
                  />
                </div>
              </div>

              {/* 부서 */}
              <InfoField
                label={<RequiredLabel>부서</RequiredLabel>}
                value={
                  <DepartmentSelect
                    selectedDepartmentId={selectedDepartmentId}
                    selectedDepartmentName={selectedDepartmentName}
                    onSelect={handleDepartmentSelect}
                    disabled={isSubmitting || isLoadingData}
                    error={departmentError}
                    placeholder="부서를 선택하세요"
                    workspaceId={selectedWorkspace?.id || ''}
                    departments={departments}
                    isLoading={isDepartmentsLoading}
                    departmentsError={departmentsError}
                  />
                }
                fullWidth
              />

              {/* 담당자 */}
              <InfoField
                label={<RequiredLabel>담당자</RequiredLabel>}
                value={
                  <MemberSelect
                    label="담당자"
                    selectedMemberId={assigneeId}
                    selectedMemberName={assigneeName}
                    onSelect={handleAssigneeSelect}
                    disabled={isSubmitting || isLoadingData}
                    placeholder="담당자를 선택하세요"
                    workspaceId={selectedWorkspace?.id || ''}
                    members={workspaceMembers}
                    isLoading={isMembersLoading}
                    membersError={membersError}
                  />
                }
                fullWidth
              />
            </div>
          </div>

          {/* 성과 항목 */}
          <div className="space-y-2">
            <div className="flex items-center justify-between pb-2 border-b" style={{
              borderBottomColor: '#EBEBEB',
              borderBottomWidth: '1px',
              padding: '7px 0px'
            }}>
              <h3 style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px',
                fontWeight: 700,
                color: designTokens.colors.gray[6]
              }}>
                성과 항목
              </h3>
              <Button
                size="small"
                color="dark"
                onClick={handleAddItem}
                disabled={isSubmitting || isLoadingData}
              >
                + 항목 추가
              </Button>
            </div>

            <div className="space-y-3" style={{ padding: '0px 4px' }}>
              {performanceItems.length === 0 ? (
                <div
                  className="p-4 rounded text-center"
                  style={{
                    backgroundColor: designTokens.colors.gray[0],
                    border: `1px solid ${designTokens.colors.gray[1]}`,
                  }}
                >
                  <span
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      color: designTokens.colors.gray[4],
                    }}
                  >
                    성과 항목이 없습니다. '+ 항목 추가' 버튼을 클릭하여 추가해주세요.
                  </span>
                </div>
              ) : (
                <>
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 rounded" style={{
                    backgroundColor: designTokens.colors.gray[0]
                  }}>
                    <div className="col-span-5">
                      <span style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        fontWeight: 600,
                        color: designTokens.colors.gray[6]
                      }}>
                        항목명
                      </span>
                    </div>
                    <div className="col-span-3">
                      <span style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        fontWeight: 600,
                        color: designTokens.colors.gray[6]
                      }}>
                        목표
                      </span>
                    </div>
                    <div className="col-span-3">
                      <span style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        fontWeight: 600,
                        color: designTokens.colors.gray[6]
                      }}>
                        달성
                      </span>
                    </div>
                    <div className="col-span-1"></div>
                  </div>

                  {/* Table Rows */}
                  {performanceItems.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-5">
                        <Input
                          value={item.name}
                          onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                          placeholder="항목명"
                          disabled={isSubmitting || isLoadingData}
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          value={item.target ?? ''}
                          onChange={(e) => handleItemChange(index, 'target', e.target.value ? parseFloat(e.target.value) : null)}
                          placeholder="목표"
                          disabled={isSubmitting || isLoadingData}
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          value={item.achievement ?? ''}
                          onChange={(e) => handleItemChange(index, 'achievement', e.target.value ? parseFloat(e.target.value) : null)}
                          placeholder="달성"
                          disabled={isSubmitting || isLoadingData}
                        />
                      </div>
                      <div className="col-span-1">
                        <Button
                          size="small"
                          color="white"
                          onClick={() => handleRemoveItem(index)}
                          disabled={isSubmitting || isLoadingData}
                        >
                          삭제
                        </Button>
                      </div>
                    </div>
                  ))}

                  {/* Total Row */}
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 rounded" style={{
                    backgroundColor: designTokens.colors.primary.light + '20',
                    borderTop: `2px solid ${designTokens.colors.primary.DEFAULT}`
                  }}>
                    <div className="col-span-5 flex items-center">
                      <span style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        fontWeight: 700,
                        color: designTokens.colors.gray[6]
                      }}>
                        합계
                      </span>
                    </div>
                    <div className="col-span-3 flex items-center">
                      <span style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        fontWeight: 700,
                        color: designTokens.colors.primary.DEFAULT
                      }}>
                        {totalTarget.toLocaleString()}
                      </span>
                    </div>
                    <div className="col-span-3 flex items-center">
                      <span style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        fontWeight: 700,
                        color: designTokens.colors.primary.DEFAULT
                      }}>
                        {totalAchievement.toLocaleString()}
                      </span>
                    </div>
                    <div className="col-span-1"></div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </SidePanel>
    </>
  )
}

export default PerformanceEditPanel
